import { useAuth0 } from "@auth0/auth0-react";
import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import useIsIntegrated from "../../../api/hooks/useIsIntegrated";
import ContainedButton from "../../../components/buttons/ContainedButton";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import SvgToIcon from "../../../icons/SvgToIcon";
import { sidebarIcons } from "../../../scenes/stack/environment/icons/iconExport";
import {
  IntegrationCategory,
  IntegrationsMapping,
} from "../../../scenes/stack/integrations/IntegrationsMapping";
import { ONBOARDING_STEPS } from "../../../utils/constants";
const WelcomeStep = () => {
  const colors = useColors();
  const { setOnboardingStage } = useOnboarding();
  const { trackEvent } = useAPI();
  const { user } = useAuth0();
  const { integrations } = useIsIntegrated();

  const checkOneEDRIntegrated = useMemo(() => {
    const edrIntegrations = Object.values(IntegrationsMapping).filter(
      (integration) => integration.category === IntegrationCategory.EDR
    );

    return edrIntegrations.some((integration) =>
      integrations.includes(integration.codeName)
    );
  }, [integrations]);

  const handleNextStep = () => {
    trackEvent(
      checkOneEDRIntegrated
        ? "User moved to learn basics step after checking one EDR integrated"
        : "User moved to connect tools step"
    );
    if (checkOneEDRIntegrated) {
      setOnboardingStage(ONBOARDING_STEPS.LEARN_BASICS);
    } else {
      setOnboardingStage(ONBOARDING_STEPS.CONNECT_TOOLS);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        gap: 3,
        p: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <SvgToIcon
          IconLogo={sidebarIcons.LogoExpanded}
          width={"100px"}
          style={{
            mb: 2,
          }}
        />
        <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
          Welcome to Breeze, {user?.name}!{" "}
          <Box
            component="img"
            src="/onboarding/hey.gif"
            alt="Waving hand"
            sx={{
              height: "30px",
              width: "30px",
              marginLeft: "5px",
              display: "inline-block",
            }}
          />
        </Typography>
        <Box>
          <Typography variant="body1">
            Breeze helps you clearly see and strengthen your security
            posture-using tools you already trust.
          </Typography>
          <Typography variant="body1">
            By integrating <strong>just one tool</strong>, Breeze can begin
            mapping your environment, surfacing controls, and highlighting risks
            that might otherwise go unnoticed.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.common_white,
          borderRadius: "8px",
          border: `1px solid ${colors.divider2}`,
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="subtitle1">What to expect next?</Typography>

        <Box sx={{ display: "flex", mb: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              bgcolor: colors.primary_main,
              color: "white",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              mt: 0.5,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            1
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
              Connect a tool
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start with your EDR to surface meaningful insights and exposures
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", mb: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              bgcolor: colors.primary_main,
              color: "white",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              mt: 0.5,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            2
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
              Add more tools
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Broaden your visibility by integrating Cloud, Identity, and
              Communication systems
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", mb: 2, alignItems: "flex-start" }}>
          <Box
            sx={{
              bgcolor: colors.primary_main,
              color: "white",
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              mt: 0.5,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            3
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
              Learn the basics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get familiar with Breeze's core features and how to use them to
              improve your security posture
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: "auto", display: "flex", justifyContent: "center" }}>
        <ContainedButton
          color="primary"
          size="large"
          onClick={handleNextStep}
          fullWidth
        >
          Let's Begin
        </ContainedButton>
      </Box>
    </Box>
  );
};

export default WelcomeStep;
